import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

/**
 * Exibido quando o backend retorna needs_tenant=true no login.
 * O usuário deve informar o slug do tenant ao qual deseja se vincular.
 * Props:
 *   credential — o token a reenviar (idToken do Google ou identity token da Apple)
 *   provider   — 'google' (padrao) ou 'apple'. Decide a rota e o nome do campo,
 *                que diferem entre os dois: o backend espelha o vocabulario de
 *                cada provedor em vez de inventar um terceiro.
 */
const TenantSelect = ({ credential, provider = 'google' }) => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!slug.trim()) {
      setError("Informe o identificador da empresa.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const ehApple = provider === 'apple';
      const res = await api.post(ehApple ? "/auth/apple" : "/auth/google", {
        ...(ehApple ? { identity_token: credential } : { credential }),
        tenant_slug: slug.trim().toLowerCase(),
      });

      if (res.data.needs_tenant) {
        setError("Empresa não encontrada. Verifique o identificador.");
        setLoading(false);
        return;
      }

      const { access_token, refresh_token, is_admin, is_superadmin, tenant } = res.data;
      login(access_token, is_admin, refresh_token, tenant, is_superadmin);
    } catch (err) {
      const msg = err.response?.data?.erro || "Erro ao vincular. Tente novamente.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>Vincular Conta</h2>
        <p style={styles.subtitle}>
          É seu primeiro acesso. Informe o identificador da empresa.
        </p>

        <div style={styles.divider} />

        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Identificador da empresa (slug)</label>
            <input
              type="text"
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
              }
              placeholder="ex: confeitaria-doce-mel"
              style={styles.input}
              autoFocus
            />
          </div>

          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? "Vinculando..." : "Entrar"}
          </button>
        </form>

        <div style={styles.divider} />

        <p style={styles.footerLink}>
          Não tem empresa cadastrada?{" "}
          <span style={styles.link} onClick={() => navigate("/register")}>
            Criar conta
          </span>
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(145deg, #f7c5c2 0%, #e8a09c 50%, #d4807c 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Segoe UI', sans-serif",
  },
  card: {
    background: "rgba(255, 255, 255, 0.92)",
    backdropFilter: "blur(12px)",
    borderRadius: "24px",
    padding: "48px 40px",
    width: "380px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(180, 80, 80, 0.2)",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2c3e50",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: "13px",
    color: "#7f8c8d",
    margin: "0 0 20px 0",
  },
  divider: {
    height: "1px",
    background: "linear-gradient(to right, transparent, #e8a09c, transparent)",
    marginBottom: "20px",
  },
  error: {
    color: "#c0392b",
    fontSize: "13px",
    marginBottom: "16px",
    background: "#fdecea",
    borderRadius: "8px",
    padding: "8px 12px",
  },
  field: {
    textAlign: "left",
    marginBottom: "16px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#555",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },
  btn: {
    width: "100%",
    padding: "12px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #e8a09c 0%, #d4807c 100%)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "16px",
  },
  footerLink: {
    fontSize: "13px",
    color: "#999",
  },
  link: {
    color: "#d4807c",
    cursor: "pointer",
    fontWeight: "600",
  },
};

export default TenantSelect;
